/**
 * Enrichment providers report failures as free-text strings that vary across
 * the ~15 supported providers and are technical by nature, e.g.
 *   "Apollo API error: 429 - {raw body}"
 *   "People Data Labs API key not configured"
 *   "provider timed out after 600s"
 *   "No match found"
 *   "API key for openai not configured in server settings"
 *
 * This module classifies a raw provider error into a short, user-friendly
 * explanation for display on the Contacts screen. It is intentionally a
 * frontend-only presentation layer so a single mapping covers every provider
 * and every enrichment code path without touching backend adapters.
 */

export type EnrichmentErrorCategory =
  | 'missing_key'
  | 'server_config'
  | 'invalid_key'
  | 'plan'
  | 'rate_limit'
  | 'credits'
  | 'no_data'
  | 'timeout'
  | 'network'
  | 'provider_outage'
  | 'unknown';

export interface EnrichmentErrorExplanation {
  category: EnrichmentErrorCategory;
  message: string;
}

/**
 * Classify a raw provider error string into a friendly category + message.
 * `providerLabel` is woven into the copy (e.g. "Apollo", "PDL"); it defaults to
 * a generic subject so the function is safe to call without one.
 */
export function classifyEnrichmentError(
  rawError?: string | null,
  providerLabel = 'This provider',
): EnrichmentErrorExplanation {
  const p = providerLabel;
  const raw = (rawError ?? '').toString();
  const e = raw.toLowerCase();

  const has = (...needles: (string | RegExp)[]) =>
    needles.some((n) => (typeof n === 'string' ? e.includes(n) : n.test(e)));

  // Order matters: most specific patterns are checked first.

  // Empty / missing error text.
  if (!raw.trim()) {
    return {
      category: 'unknown',
      message: `${p} couldn't complete enrichment. Please try again.`,
    };
  }

  // Server-side config (native AI providers whose keys live in server settings,
  // not the user's Integrations page). Checked before generic missing-key.
  if (has('server settings', 'in server settings')) {
    return {
      category: 'server_config',
      message: `${p} enrichment isn't configured on the server yet. Please contact your administrator.`,
    };
  }

  // No API key set up by the user.
  if (has('not configured', 'no api key', 'api key not set', 'key is missing', 'not set up')) {
    return {
      category: 'missing_key',
      message: `No ${p} API key is set up. Add your key under Settings > Integrations to use this provider.`,
    };
  }

  // Out of credits / quota exhausted.
  if (has('insufficient', 'out of credit', 'no credit', 'not enough credit', 'quota exceeded', 'quota', 'exhausted', 'payment required', '402')) {
    return {
      category: 'credits',
      message: `Your ${p} account is out of credits or has hit its quota. Top up or check your ${p} plan.`,
    };
  }

  // Rate limited.
  if (has('429', 'rate limit', 'rate limited', 'too many requests')) {
    return {
      category: 'rate_limit',
      message: `${p} is receiving too many requests right now. Please wait a few minutes and try again.`,
    };
  }

  // Invalid / expired credentials.
  if (has('401', 'unauthorized', 'invalid api key', 'invalid key', 'authentication', 'auth failed')) {
    return {
      category: 'invalid_key',
      message: `Your ${p} API key looks invalid or expired. Check it under Settings > Integrations.`,
    };
  }

  // Plan / permission (403).
  if (has('403', 'forbidden', 'master api key', /requires a .*plan/, /check your .*plan/, 'not allowed on your plan')) {
    return {
      category: 'plan',
      message: `Your ${p} plan doesn't allow this lookup. Upgrade or verify your ${p} plan permissions.`,
    };
  }

  // No matching data for this contact.
  if (has('no match', 'no record', 'no data', 'no results', 'not found', 'different contact')) {
    return {
      category: 'no_data',
      message: `${p} had no matching data for this contact.`,
    };
  }

  // Timeout.
  if (has('timed out', 'timeout', 'took too long')) {
    return {
      category: 'timeout',
      message: `${p} took too long to respond. Please try again shortly.`,
    };
  }

  // Network / connectivity.
  if (has('network error', 'connection', 'econnreset', 'econnrefused', 'dns', 'unreachable')) {
    return {
      category: 'network',
      message: `We couldn't reach ${p}. Check your connection and try again.`,
    };
  }

  // Provider-side outage (5xx).
  if (has('500', '502', '503', '504', 'server error', 'internal server', 'bad gateway', 'service unavailable', 'gateway timeout')) {
    return {
      category: 'provider_outage',
      message: `${p} is temporarily unavailable. Please try again later.`,
    };
  }

  // Anything else: keep it friendly rather than leaking the raw string.
  return {
    category: 'unknown',
    message: `${p} couldn't complete enrichment. Please try again.`,
  };
}

/** Convenience wrapper returning just the friendly message string. */
export function explainEnrichmentError(
  rawError?: string | null,
  providerLabel?: string,
): string {
  return classifyEnrichmentError(rawError, providerLabel).message;
}

/**
 * Friendly text for a run-level (aggregate) enrichment failure, i.e. the single
 * top-level error surfaced by the SSE stream or the bulk endpoint (e.g.
 * "no provider completed successfully", "enrichment timed out after 5 minutes",
 * "No API key configured for apollo. Set it in your .env file."). This is not
 * per-provider; per-provider detail is shown separately via SourceBadges.
 */
export function explainEnrichmentRunError(rawError?: string | null): string {
  const e = (rawError ?? '').toString().toLowerCase();
  if (!e.trim()) return "Enrichment couldn't be completed. Please try again.";
  if (e.includes('timed out') || e.includes('timeout') || e.includes('took too long')) {
    return 'Enrichment took too long and timed out. Please try again.';
  }
  if (e.includes('no provider')) {
    return 'None of the selected providers could enrich this contact. Check the per-provider details below, or try again.';
  }
  if (e.includes('not configured') || e.includes('no api key') || e.includes('.env')) {
    return 'A provider API key is not set up. Add it under Settings > Integrations and try again.';
  }
  return "Enrichment couldn't be completed. Please try again.";
}

/**
 * Whether a failed provider is worth surfacing as a message under the source
 * pills.
 *
 * SEC EDGAR only holds filings for publicly listed US companies, so "no
 * filings for this company" is the expected outcome for most B2B prospects,
 * not a fault the user can act on — and the raw string it returns
 * ("Could not find SEC filings for '<Company>' (may be private or non-US)")
 * lands in the `unknown` bucket, which renders as a misleading "Please try
 * again". Suppress those; anything actionable (bad key, out of credits, rate
 * limit, provider outage) still gets a message.
 */
const SILENT_SEC_CATEGORIES: EnrichmentErrorCategory[] = ['no_data', 'unknown'];

export function shouldShowEnrichmentFailure(
  providerKey: string,
  rawError?: string | null,
): boolean {
  if (providerKey !== 'sec_edgar') return true;
  const { category } = classifyEnrichmentError(rawError);
  return !SILENT_SEC_CATEGORIES.includes(category);
}

const SEC_NO_FILINGS_MESSAGE = 'No SEC filings — the company is private or non-US.';
const SEC_NO_COMPANY_MESSAGE = 'No company name on this contact, so there was nothing to look up.';

/**
 * Friendly text for a failed provider, aware of which provider it came from.
 *
 * Use this anywhere a failure is shown to the user (pill tooltips, the message
 * list). It differs from `explainEnrichmentError` only for the cases
 * `shouldShowEnrichmentFailure` suppresses: those are normal outcomes rather
 * than faults, so they get accurate copy instead of the generic "Please try
 * again" — which matters because the pill tooltip still shows on hover even
 * when the message row is hidden.
 */
export function explainEnrichmentFailure(
  providerKey: string,
  rawError?: string | null,
  providerLabel?: string,
): string {
  if (providerKey === 'sec_edgar' && !shouldShowEnrichmentFailure(providerKey, rawError)) {
    const e = (rawError ?? '').toString().toLowerCase();
    return e.includes('no company name') ? SEC_NO_COMPANY_MESSAGE : SEC_NO_FILINGS_MESSAGE;
  }
  return explainEnrichmentError(rawError, providerLabel);
}
